import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { history, useLocation } from 'umi';
import { Layout } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getLanguage } from '@home/utils/common';
import type { IStudioQueryTabsProps } from './types';
import StudioTabHeaderInfo, { isStudioTabInfoVisible } from './StudioTabHeaderInfo';
import '@/pages/home/assets/scss/page/home/index.scss';
import './index.scss';

const { Content } = Layout;

function resolveActiveKey(
  tabs: NonNullable<IStudioQueryTabsProps['tabs']>,
  activeKey: string | undefined,
  queryValue: string | null,
  defaultActiveKey: string | undefined,
) {
  const validKeys = new Set(tabs.map((tab) => tab.key));
  if (activeKey && validKeys.has(activeKey)) {
    return activeKey;
  }
  if (queryValue && validKeys.has(queryValue)) {
    return queryValue;
  }
  if (defaultActiveKey && validKeys.has(defaultActiveKey)) {
    return defaultActiveKey;
  }
  return tabs[0]?.key;
}

const StudioQueryTabs: React.FC<IStudioQueryTabsProps> = ({
  tabs = [],
  titleEn,
  titleCn,
  descriptionEn,
  descriptionCn,
  activeKey,
  defaultActiveKey,
  queryKey = 'tab',
  onChange,
  className,
  info,
}) => {
  const location = useLocation();
  const { isEN } = useLocal();
  const isUserActionRef = useRef(false);
  const hasTitle = !!(titleEn || titleCn);
  const hasDescription = !!(descriptionEn || descriptionCn);
  const hasTabs = tabs.length > 0;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryValue = searchParams.get(queryKey);
  const resolvedActiveKey = hasTabs
    ? resolveActiveKey(tabs, activeKey, queryValue, defaultActiveKey)
    : undefined;

  useEffect(() => {
    if (!hasTabs || !resolvedActiveKey || queryValue === resolvedActiveKey) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    nextParams.set(queryKey, resolvedActiveKey);
    history.replace(`${location.pathname}?${nextParams.toString()}`);
  }, [hasTabs, location.pathname, location.search, queryKey, queryValue, resolvedActiveKey]);

  const tabItems = useMemo(
    () => tabs.map((tab) => ({
      id: tab.key,
      label: (
        <span className="dbi-sourcelabel-text">
          {getLanguage(isEN, tab.labelEn, tab.labelCn)}
        </span>
      ),
    })),
    [isEN, tabs],
  );

  const tabAddon = useMemo(() => {
    if (!isStudioTabInfoVisible(info)) {
      return undefined;
    }
    return <StudioTabHeaderInfo info={info} />;
  }, [info]);

  if (!hasTitle && !hasTabs && !tabAddon) {
    return null;
  }

  const handleActive = (tab: { id: string; }) => {
    if (tab.id === resolvedActiveKey) {
      return;
    }
    isUserActionRef.current = true;
    const nextParams = new URLSearchParams(location.search);
    nextParams.set(queryKey, tab.id);
    history.replace(`${location.pathname}?${nextParams.toString()}`);
    onChange?.(tab.id);
    isUserActionRef.current = false;
  };

  const title = getLanguage(isEN, titleEn, titleCn);
  const description = getLanguage(isEN, descriptionEn, descriptionCn);

  const tabDom = hasTabs ? (
    <div className="header-tab">
      <Tabs
        ceiling
        tabs={tabItems}
        animated={false}
        activeId={resolvedActiveKey}
        onActive={handleActive}
        newMarkIdsMappingKey=""
        addon={tabAddon}
      />
    </div>
  ) : (
    tabAddon && (
      <div className="dbh-studio-query-tabs-info-standalone">
        {tabAddon}
      </div>
    )
  );

  if (hasTitle) {
    return (
      <Content.Header
        className={classNames('dbi-header contentHead-normal', className)}
        title={title}
      >
        {hasDescription && (
          <div className="header-des">
            <span className="header-des-text">{description}</span>
          </div>
        )}
        {tabDom}
      </Content.Header>
    );
  }

  return (
    <Content.Header className={classNames('dbh-studio-query-tabs-header', className)}>
      {tabDom}
    </Content.Header>
  );
};

export default StudioQueryTabs;
